import tenantConstants from '@constants';
import tenantData from '@data';
import { useMemo } from 'react';
import useElementObserver from './useElementObserver';
import { t } from 'i18next';
import { isGCCTenant } from '../utility/env';

// TruLeads revamp: on Bayut KSA the walk-through is exactly four taps — one on the leads
// table and three inside the lead detail drawer (interactions, Add Task, All Tasks). The
// standalone TruLeads dashboard is retired there (/lms/truleads redirects to the unified
// leads page), so its two taps are hidden behind the same flag. eg/oman lack the flag and
// keep the dashboard walk-through unchanged.
const IS_UNIFIED_LEADS_PAGE = !!tenantConstants.LMS_ENABLED?.IS_UNIFIED_LEADS_PAGE;
const EMPTY_STEPS = [];

const useGetTourStepsForLms = (isMobile, data, sectionKey) => {
  const { element } = useElementObserver(`#${tenantData.tourElementIds.leads_dashboard_first_row}`);
  const tapTargets = JSON.parse(localStorage.getItem('tapTargets'));

  const leadsDashboardSteps = useMemo(
    () =>
      IS_UNIFIED_LEADS_PAGE
        ? EMPTY_STEPS
        : [
            ...(element
              ? [
                  {
                    title: <br />,
                    placement: 'top',
                    description: t(
                      'As you receive new leads, they will appear here with their latest interactions, interested listings, and assigned tasks for streamlined tracking and follow-ups.',
                    ),
                    target: () => element,
                  },
                ]
              : []),
            {
              title: <br />,
              placement: 'left',
              description: t('Access a comprehensive list of all your leads.'),
              target: () => document.getElementById(tenantData.tourElementIds.leads_dashboard_view_all_link),
              btxText: t('Finish'),
            },
          ],
    [data, element],
  );
  const leadsManagementSteps = useMemo(
    () => [
      ...(data?.length
        ? [
            {
              title: <br />,
              placement: 'top',
              description: t(
                'See all your leads with their status, details, and interaction history for easier follow-up.',
              ),
              target: () => document.getElementById(tenantData.tourElementIds.leads_management_table),
              btxText: t('Finish'),
            },
          ]
        : []),
    ],
    [data],
  );

    /**
   * The tour id sits on the drawer's listing container, which also covers the empty filter row and
   * the padding above the interactions table, so the highlight reached up past the table and into
   * the drawer header. Aim at the table when there is one; the mobile card list has none, so the
   * container stays as the fallback.
   */
    const getLeadInterestsTarget = () => {
      const container = document.getElementById(tenantData.tourElementIds.lead_detail_all_interests);
      return container?.querySelector('.ant-table-wrapper') || container;
    };

  // Desktop: the drawer is pinned to the edge of the viewport, so each bubble sits to its
  // left, its arrow centred on the element it describes — the interactions table, then the
  // Add Task button, then the All Tasks tab. In Arabic the drawer opens on the other side
  // and antd flips the bubble across on its own. Mobile placements are left as they were.
  const leadDetailDrawerSteps = useMemo(
    () => [
      {
        title: <br />,
        placement: isMobile ? 'topLeft' : 'left',
        description: t('Review all interactions for this lead, including lead sources, interested listings and date.'),
        target: () => document.getElementById(tenantData.tourElementIds.lead_detail_all_interests),
      },
      ...(isGCCTenant
        ? []
        : [
            {
              title: <br />,
              placement: isMobile ? 'top' : 'left',
              description: t(
                'Create a task against each lead to ensure timely follow-ups and effective lead management.',
              ),
              target: () => document.getElementById(tenantData.tourElementIds.lead_detail_add_tasks),
              scrollIntoViewOptions: false,
            },
            {
              title: <br />,
              placement: isMobile ? 'bottom' : 'left',
              description: t('Access and manage all tasks associated with your leads'),
              target: () => document.getElementById(tenantData.tourElementIds.lead_detail_all_tasks_tab),
              scrollIntoViewOptions: false,
              btxText: t('Finish'),
            },
          ]),
    ],
    [isMobile],
  );

  const stepsKeyMappings = {
    dashboard: leadsDashboardSteps,
    management: leadsManagementSteps,
    lead_detail: leadDetailDrawerSteps,
  };

  // A section can legitimately produce zero steps (the leads management tour is empty until the
  // agent has at least one lead). Rendering a tour anyway leaves antd's full-screen mask dimming
  // the page with nothing highlighted and no popover to dismiss it, so report "no tour" instead.
  const getShowTour = () => {
    return !tapTargets?.lms?.[sectionKey]?.hide;
  };
  const onCloseTour = () => {
    localStorage.setItem(
      'tapTargets',
      JSON.stringify({
        ...(tapTargets && tapTargets),
        lms: {
          ...(tapTargets?.lms && tapTargets?.lms),
          [sectionKey]: {
            hide: true,
          },
        },
      }),
    );
  };

  const sectionSteps = stepsKeyMappings?.[sectionKey] ?? EMPTY_STEPS;

  // Keep the array identity stable: antd re-resolves the step target every time `steps`
  // changes, so rebuilding it on each render of the host page re-runs that work needlessly.
  const steps = useMemo(
    () =>
      sectionSteps.map((item) => ({
        ...item,
        prevButtonProps: {
          children: <>{t('Previous')}</>,
        },
        nextButtonProps: {
          children: <>{t(item?.btxText || t('Next'))}</>,
        },
      })),
    [sectionSteps],
  );

  return {
    steps,
    SHOW_TOUR: getShowTour(),
    onCloseTour: onCloseTour,
  };
};

export default useGetTourStepsForLms;
